import type { AxiosResponse } from 'axios'

import type {
    AddCooperationJobCommentPayload,
    CooperationJobComment,
    CooperationJobConsumptionMaterialTypesResponse,
    CooperationJobResponse,
    CooperationJobsListResponse,
    CooperationJobStatusesResponse,
    UpdateCooperationJobPayload
} from '@/api/modules/cooperationJobsQueries'
import cooperationJobsQueries from '@/api/modules/cooperationJobsQueries'
import { t } from '@/i18n'
import { notificationService } from '@/services/notificationService'
import { handleApiError } from '@/utils/apiHelpers'

async function fetchCooperationJobs(queryString = ''): Promise<AxiosResponse<CooperationJobsListResponse>> {
    try {
        return await cooperationJobsQueries.fetchCooperationJobs(queryString)
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.fetchList'))
        throw error
    }
}

async function fetchCooperationJobStatuses(): Promise<AxiosResponse<CooperationJobStatusesResponse>> {
    try {
        return await cooperationJobsQueries.fetchCooperationJobStatuses()
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.fetchStatuses'))
        throw error
    }
}

async function fetchConsumptionMaterialTypes(): Promise<AxiosResponse<CooperationJobConsumptionMaterialTypesResponse>> {
    try {
        return await cooperationJobsQueries.fetchConsumptionMaterialTypes()
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.fetchConsumptionMaterialTypes'))
        throw error
    }
}

async function generateCooperationJobsReport(queryString: string, userEmail: string | null): Promise<AxiosResponse<unknown>> {
    try {
        const response = await cooperationJobsQueries.generateCooperationJobsReport(queryString)
        notificationService.success(t('cooperationJobs.notifications.reportSuccess', { email: userEmail }))
        return response
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.generateReport'))
        throw error
    }
}

async function fetchCooperationJob(cooperationJobId: number | string): Promise<AxiosResponse<CooperationJobResponse>> {
    try {
        return await cooperationJobsQueries.fetchCooperationJob(cooperationJobId)
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.fetchDetails'))
        throw error
    }
}

async function updateCooperationJob(
    cooperationJobId: number | string,
    payload: UpdateCooperationJobPayload
): Promise<AxiosResponse<CooperationJobResponse>> {
    try {
        const response = await cooperationJobsQueries.updateCooperationJob(cooperationJobId, payload)
        notificationService.success(t('cooperationJobs.notifications.updateSuccess'))
        return response
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.update'))
        throw error
    }
}

async function addCooperationJobComment(payload: AddCooperationJobCommentPayload): Promise<AxiosResponse<CooperationJobComment>> {
    try {
        const response = await cooperationJobsQueries.addCooperationJobComment(payload)
        notificationService.success(t('cooperationJobs.notifications.commentCreated'))
        return response
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.createComment'))
        throw error
    }
}

async function deleteCooperationJob(cooperationJobId: number): Promise<AxiosResponse<void>> {
    try {
        const response = await cooperationJobsQueries.deleteCooperationJob(cooperationJobId)
        notificationService.success(t('cooperationJobs.notifications.deleteSuccess'))
        return response
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.delete'))
        throw error
    }
}

async function printCooperationJobCards(cooperationJobId: number | string): Promise<AxiosResponse<ArrayBuffer>> {
    try {
        return await cooperationJobsQueries.printCooperationJobCards(cooperationJobId)
    } catch (error) {
        handleApiError(error, t('cooperationJobs.errors.printCards'))
        throw error
    }
}

export const cooperationJobsService = {
    fetchCooperationJobs,
    fetchCooperationJobStatuses,
    fetchConsumptionMaterialTypes,
    fetchCooperationJob,
    updateCooperationJob,
    addCooperationJobComment,
    generateCooperationJobsReport,
    deleteCooperationJob,
    printCooperationJobCards
}
